"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import { parseTagsFromStorage, normalizeTags, validateTag } from "@/lib/utils/crm/tags-utils";

export type TagEntityType = "contacts" | "companies" | "deals" | "tickets" | "tasks";

export interface UseInlineTagsEditorStateParams {
  tags: string[];
  onTagsUpdate: (newTags: string[]) => Promise<void>;
  maxTags: number;
  maxDisplayed: number;
}

export interface UseInlineTagsEditorStateResult {
  parsedTags: string[];
  displayedTags: string[];
  hiddenTags: string[];
  hiddenCount: number;
  isAddingTag: boolean;
  newTagInput: string;
  isLoading: boolean;
  popoverOpened: boolean;
  setIsAddingTag: (value: boolean) => void;
  setNewTagInput: (value: string) => void;
  setPopoverOpened: (value: boolean) => void;
  handleAddTag: () => Promise<void>;
  handleRemoveTag: (tagToRemove: string) => Promise<void>;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  cancelAddingTag: () => void;
}

export function useInlineTagsEditorState({
  tags,
  onTagsUpdate,
  maxTags,
  maxDisplayed,
}: UseInlineTagsEditorStateParams): UseInlineTagsEditorStateResult {
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const parsedTags = parseTagsFromStorage(tags);
  const displayedTags = parsedTags.slice(0, maxDisplayed);
  const hiddenTags = parsedTags.slice(maxDisplayed);
  const hiddenCount = hiddenTags.length;

  const runAsyncUpdate = useCallback(
    async (operation: () => Promise<void>, successMessage: string) => {
      try {
        if (isMountedRef.current) {
          setIsLoading(true);
        }
        await operation();
        if (isMountedRef.current) {
          toast.success(successMessage);
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error("Tag update failed:", error);
          toast.error(error instanceof Error ? error.message : "Tag update failed");
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  const handleAddTag = useCallback(async () => {
    const trimmed = newTagInput.trim();
    if (!trimmed) {
      toast.error("Please enter a tag");
      return;
    }

    const validation = validateTag(trimmed);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid tag");
      return;
    }

    if (parsedTags.includes(trimmed.toLowerCase())) {
      toast.error("This tag already exists");
      return;
    }

    if (parsedTags.length >= maxTags) {
      toast.error(`Maximum ${maxTags} tags allowed`);
      return;
    }

    await runAsyncUpdate(async () => {
      const newTags = normalizeTags([...parsedTags, trimmed]);
      await onTagsUpdate(newTags);
      if (isMountedRef.current) {
        setNewTagInput("");
        setIsAddingTag(false);
      }
    }, "Tag added successfully");
  }, [newTagInput, parsedTags, onTagsUpdate, maxTags, runAsyncUpdate]);

  const handleRemoveTag = useCallback(
    async (tagToRemove: string) => {
      await runAsyncUpdate(async () => {
        const newTags = parsedTags.filter((tag) => tag !== tagToRemove);
        await onTagsUpdate(newTags);
      }, "Tag removed successfully");
    },
    [parsedTags, onTagsUpdate, runAsyncUpdate]
  );

  const cancelAddingTag = useCallback(() => {
    setIsAddingTag(false);
    setNewTagInput("");
  }, []);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void handleAddTag();
      } else if (e.key === "Escape") {
        cancelAddingTag();
      }
    },
    [handleAddTag, cancelAddingTag]
  );

  return {
    parsedTags,
    displayedTags,
    hiddenTags,
    hiddenCount,
    isAddingTag,
    newTagInput,
    isLoading,
    popoverOpened,
    setIsAddingTag,
    setNewTagInput,
    setPopoverOpened,
    handleAddTag,
    handleRemoveTag,
    handleKeyPress,
    cancelAddingTag,
  };
}
